import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import ConversationItem from './components/ConversationItem';
import MessageBubble from './components/MessageBubble';
import ChatInput from './components/ChatInput';
import Image from '../../components/AppImage';
import {
  getUserConversations,
  getConversationMessages,
  sendMessage,
  markConversationAsRead,
  subscribeToMessages,
  subscribeToConversations,
  subscribeToUserPresence,
  updateMessageStatus,
  unsubscribeChannel
} from '../../services/messagingService';
import Header from '../../components/navigation/Header';


const Messages = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const messageChannelRef = useRef(null);
  const conversationChannelRef = useRef(null);
  const presenceChannelRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load conversations
  useEffect(() => {
    if (!user) return;

    const loadConversations = async () => {
      try {
        setLoading(true);
        const data = await getUserConversations();
        setConversations(data || []);
        
        // Auto-select first conversation if available
        if (data?.length > 0 && !selectedConversation) {
          setSelectedConversation(data?.[0]);
        }
      } catch (err) {
        console.error('Error loading conversations:', err);
        setError('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    loadConversations();

    // Subscribe to conversation updates
    if (user?.id) {
      conversationChannelRef.current = subscribeToConversations(user?.id, (updatedConv) => {
        setConversations(prev => {
          const index = prev?.findIndex(c => c?.id === updatedConv?.id);
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = { ...updated?.[index], ...updatedConv };
            return updated;
          }
          return prev;
        });
      });
    }

    return () => {
      if (conversationChannelRef?.current) {
        unsubscribeChannel(conversationChannelRef?.current);
      }
    };
  }, [user]);

  // Load messages when conversation is selected
  useEffect(() => {
    if (!selectedConversation?.id) return;

    const loadMessages = async () => {
      try {
        setMessagesLoading(true);
        const data = await getConversationMessages(selectedConversation?.id);
        setMessages(data || []);
        
        // Mark as read
        await markConversationAsRead(selectedConversation?.id);
        
        // Update unread count in conversations list
        setConversations(prev => 
          prev?.map(c => 
            c?.id === selectedConversation?.id 
              ? { ...c, unreadCount: 0 } 
              : c
          )
        );
        
        // Mark messages as delivered
        const undeliveredMessages = data?.filter(m => m?.senderId !== user?.id && m?.status === 'sent');
        for (const msg of undeliveredMessages) {
          await updateMessageStatus(msg?.id, 'delivered');
        }
        
        setTimeout(scrollToBottom, 100);
      } catch (err) {
        console.error('Error loading messages:', err);
      } finally {
        setMessagesLoading(false);
      }
    };

    loadMessages();

    // Subscribe to new messages
    messageChannelRef.current = subscribeToMessages(
      selectedConversation?.id,
      (payload) => {
        if (payload?.type === 'INSERT') {
          setMessages(prev => [...prev, payload?.message]);
          setTimeout(scrollToBottom, 100);
          
          // Mark as read if not from current user
          if (payload?.message?.senderId !== user?.id) {
            markConversationAsRead(selectedConversation?.id);
            updateMessageStatus(payload?.message?.id, 'delivered');
          }
        } else if (payload?.type === 'UPDATE') {
          setMessages(prev => 
            prev?.map(m => m?.id === payload?.message?.id ? payload?.message : m)
          );
        }
      }
    );

    // Subscribe to presence (online status)
    if (user?.id) {
      presenceChannelRef.current = subscribeToUserPresence(
        selectedConversation?.id,
        user?.id,
        (presencePayload) => {
          if (presencePayload?.type === 'PRESENCE_SYNC') {
            setOnlineUsers(presencePayload?.onlineUsers || []);
          } else if (presencePayload?.type === 'USER_JOINED') {
            setOnlineUsers(prev => [...new Set([...prev, presencePayload?.userId])]);
          } else if (presencePayload?.type === 'USER_LEFT') {
            setOnlineUsers(prev => prev?.filter(id => id !== presencePayload?.userId));
          }
        }
      );
    }

    return () => {
      if (messageChannelRef?.current) {
        unsubscribeChannel(messageChannelRef?.current);
      }
      if (presenceChannelRef?.current) {
        unsubscribeChannel(presenceChannelRef?.current);
      }
    };
  }, [selectedConversation?.id, user?.id]);

  // Handle sending message
  const handleSendMessage = async (content) => {
    if (!content?.trim() || !selectedConversation?.id) return;

    try {
      await sendMessage(selectedConversation?.id, content);
      // Message will be added via real-time subscription
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    }
  };

  // Handle conversation selection
  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
  };

  // Filter conversations
  const filteredConversations = conversations?.filter((c) => {
    const otherParticipant = c?.participants?.[0];
    const searchLower = searchQuery?.toLowerCase();
    return (
      otherParticipant?.fullName?.toLowerCase()?.includes(searchLower) ||
      otherParticipant?.email?.toLowerCase()?.includes(searchLower) ||
      c?.lastMessage?.content?.toLowerCase()?.includes(searchLower)
    );
  });

  // Get active conversation display data
  const activeConversation = selectedConversation ? {
    ...selectedConversation,
    otherParticipant: selectedConversation?.participants?.[0],
    isOnline: onlineUsers?.includes(selectedConversation?.participants?.[0]?.id)
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-[60px] lg:pt-[60px] flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-[#1E73BE] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-[60px] lg:pt-[60px]">
        <div className="h-[calc(100vh-60px)] flex">
          {/* Conversations List */}
          <div className="w-full md:w-[380px] lg:w-[420px] border-r border-gray-200 bg-white flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-br from-blue-50 to-orange-50">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-heading font-bold text-gray-900 flex items-center gap-3">
                  <Icon name="MessageSquare" size={28} className="text-[#1E73BE]" />
                  Messages
                </h1>
                <button className="p-2.5 rounded-lg hover:bg-white/50 transition-colors">
                  <Icon name="Settings" size={20} className="text-gray-600" />
                </button>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {error && (
                <div className="p-4 bg-red-50 border-b border-red-200">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              
              {filteredConversations?.length > 0 ? (
                filteredConversations?.map((conversation) => {
                  const otherParticipantId = conversation?.participants?.[0]?.id;
                  const isOnline = onlineUsers?.includes(otherParticipantId);
                  
                  return (
                    <ConversationItem
                      key={conversation?.id}
                      conversation={conversation}
                      isActive={selectedConversation?.id === conversation?.id}
                      isOnline={isOnline}
                      onClick={() => handleSelectConversation(conversation)}
                    />
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <Icon name="MessageSquare" size={48} className="text-gray-400 mb-4" />
                  <p className="text-gray-600 font-medium">
                    {searchQuery ? 'No conversations found' : 'No conversations yet'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    {searchQuery ? 'Try a different search term' : 'Start a conversation from a job posting'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="hidden md:flex flex-1 flex-col bg-gray-50">
            {activeConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-6 border-b border-gray-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <Image
                          src={activeConversation?.otherParticipant?.avatarUrl || 'https://via.placeholder.com/40'}
                          alt={`${activeConversation?.otherParticipant?.fullName || 'User'} profile picture`}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        {activeConversation?.isOnline && (
                          <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-[#5CD600] border-2 border-white rounded-full" />
                        )}
                      </div>
                      <div>
                        <h2 className="font-semibold text-base text-gray-900">
                          {activeConversation?.otherParticipant?.fullName || 'Unknown User'}
                        </h2>
                        <p className="text-sm text-gray-600">
                          {activeConversation?.isOnline ? (
                            <span className="text-[#5CD600] font-medium">Online</span>
                          ) : (
                            activeConversation?.otherParticipant?.email || ''
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2.5 rounded-lg hover:bg-gray-100 transition-colors">
                        <Icon name="Phone" size={18} className="text-gray-600" />
                      </button>
                      <button className="p-2.5 rounded-lg hover:bg-gray-100 transition-colors">
                        <Icon name="Video" size={18} className="text-gray-600" />
                      </button>
                      <button className="p-2.5 rounded-lg hover:bg-gray-100 transition-colors">
                        <Icon name="MoreVertical" size={18} className="text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <Icon name="Loader2" size={32} className="text-[#1E73BE] animate-spin" />
                    </div>
                  ) : messages?.length > 0 ? (
                    messages?.map((message) => (
                      <MessageBubble
                        key={message?.id}
                        message={message}
                        isOwn={message?.senderId === user?.id}
                      />
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-600">No messages yet. Start the conversation!</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <ChatInput onSendMessage={handleSendMessage} />
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <Icon name="MessageSquare" size={64} className="text-gray-400 mb-6" />
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Select a conversation</h2>
                <p className="text-gray-600">Choose a conversation from the list to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;